import { SelectData } from "mui-rff";

export type TApp = {
  id: string;
  name: string;
  publicKey?: string;
};

export type TCreateAppData = Omit<TApp, "id">;

export type TAppSettings = {
  onboardingConfig?: string;
};
export type TSdkSettings = {
  pullMode?: boolean;
  webhookUrl?: string;
  secretKey?: string;
};

export enum VerificationAppDocumentType {
  "MokBlock" = "NewBLock",
  "UserInformation" = "UserInformation",
  "EntityRepresentatives" = "EntityRepresentatives",
  "Residence" = "Residence",
  "FinancialInformation" = "FinancialInformation",
  "BeneficialOwnership" = "BeneficialOwnership",
  "ProofOfResidence" = "ProofOfResidence",
  "Identification" = "Identification",
  "Passport" = "Passport",
  "Id" = "Id",
  "Occupation" = "Occupation",
  "Selfie" = "Selfie",
  "EntityInformation" = "EntityInformation",
  "DocumentSigning" = "DocumentSigning",
  "Custom" = "Custom",
}
export enum TConfigItemType {
  NOTE = "note",
  COUNTRY_SELECT = "countrySelect",
  SELECT = "select",
  TEXT = "text",
  URL = "url",
  CHECKBOX = "checkbox",
  DATE = "date",
  FILE_PICKER = "filePicker",
  TAX_ID = "taxId",
  PEP = "pep",
  SOURCE_OF_ACTIVE = "sourceOfActive",
}

export enum ConditionOperator {
  EQUALS = "equals",
  NOT_EQUALS = "notEquals",
  GREATER_THAN = "greaterThan",
  LESS_THAN = "lessThan",
  IN = "in",
  NOT_IN = "notIn",
  EXISTS = "exists",
  NOT_EXISTS = "notExists",
}

export const VERIFICATION_REQUEST_CONDITION_DOC = "$VerificationRequest";

export const VERIFICATION_REQUEST_CONDITION_FIELDS = [
  "status",
  "category",
] as const;

export type ConditionFieldOperator = ConditionOperator | "condition";

export enum IdentificationProvider {
  AMLBOT = "AMLBot",
  INNOVATRICS = "Innovatrics",
  MANUAL = "Manual",
}

export enum IndividualsScreeningProvider {
  AMLBOT = "AMLBot",
  SUMSUB = "SumSub",
  MANUAL = "Manual",
}

export enum AppCategory {
  INDIVIDUAL = "individual",
  ENTITY = "entity",
}

export enum AppSettingsTabs {
  GENERAL = "?general",
  QUESTIONNAIRE = "?questionnaire",
  TEMPLATES = "?templates",
  ADVANCED = "?advanced",
}

export const AppSettingsTabsView = [
  AppSettingsTabs.GENERAL,
  AppSettingsTabs.QUESTIONNAIRE,
  AppSettingsTabs.TEMPLATES,
  AppSettingsTabs.ADVANCED,
];

export interface ICondition {
  field: string;
  op: ConditionFieldOperator;
  value?: any;
  const?: string;
  doc?: string;
}

export type ConditionExpression =
  | ICondition
  | { OR: ConditionExpression[] }
  | { NOT: ConditionExpression }
  | ConditionExpression[];

interface FieldRules {
  required?: ICondition[];
  [key: string]: ICondition[] | undefined;
}

export type DynamicString = {
  [key: string]: ConditionExpression | "default";
};

export type DynamicBoolean = ConditionExpression;

export type DynamicDefault = {
  doc?: VerificationAppDocumentType;
  field: string;
};

export type StepRequirement = {
  message: string;
  condition: ConditionExpression;
};

export interface TVerificationAppFieldConfigComponent {
  name?: string;
  label: string | DynamicString;
  subtitle?: string | DynamicString;
  content?: string;
  default?: boolean | string | number | string[] | number[] | DynamicDefault;
  multiple?: boolean;
  kinds?: string[];
  multiline?: boolean;
  rows?: number;
  data?: string[] | SelectData[];
  other?: string;
  required?: boolean | DynamicBoolean;
  visible?: DynamicBoolean;
  required_condition?: Record<string, string[]>;
  rules?: FieldRules;
}

export interface TVerificationAppFieldConfigItem
  extends TVerificationAppFieldConfigComponent {
  type: TConfigItemType;
}

export type TEntityRepresentativesApps = {
  representative: string;
  ubo: string;
};

export type TVerificationAppElement = {
  type: VerificationAppDocumentType;
  name?: string;
  title?: string;
  subtitle?: string;
  fields?: TVerificationAppFieldConfigItem[];
  requirements?: StepRequirement[];
  apps?: TEntityRepresentativesApps;
  excludedFields?: string[];
  requiredFields?: string[];
  amlbot?: {
    format?: "default" | "doc_only";
  };
};

export type TAcceptanceScreenRules = {
  showAcceptanceScreen?: boolean;
  bannedCountries?: string[];
};

export type TFineTuning = {
  document_type: string;
  allowed_countries: string[];
};

export type TInnovatricsSettings = {
  country_list?: {
    show_only: string[];
    exclude: string[];
    show_first: string[];
  };
  document_types?: {
    show_only: string[];
    exclude: string[];
    fine_tuning: TFineTuning[];
  };
  document_retries?: number;
};

export type TOnboardingConfig = {
  category?: AppCategory;
  constants?: Record<string, any>;
  conditions?: Record<string, ConditionExpression>;
  elements?: TVerificationAppElement[];
  rules?: TAcceptanceScreenRules;
  identification_provider?: IdentificationProvider;
  individuals_screening_provider?: IndividualsScreeningProvider;
  innovatrics_settings?: TInnovatricsSettings;
  document_signing?: boolean;
  poc_stellar_verifiable_creds?: boolean;
};

export interface AppSettingsTabProps {
  appId: string;
  config: TOnboardingConfig;
  onConfigChange: (updater: (prev: TOnboardingConfig) => TOnboardingConfig) => void;
}

export type TDocumentTemplate = {
  id: string;
  appId?: string;
  name: string;
  description: string;
  isActive: boolean;
  sortOrder: number;
  code: string;
};

export type TCreateDocumentTemplate = {
  name: string;
  description: string;
  is_active: boolean;
  sort_order: number;
  code: string;
};

export type TUploadTemplateRespone = {
  uploadUrl: string;
  filePath: string;
};

export type TAppTemplate = {
  id: string;
  appId: string;
  templateId: string;
  createdAt: string;
  updatedAt: string;
  template: TDocumentTemplate;
};

export type TGetAppTemplatesResponse = {
  items: TDocumentTemplate[];
};
